/*
 * SonarQube
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * mailto:info AT sonarsource DOT com
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 3 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program; if not, write to the Free Software Foundation,
 * Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 */

import { MessageCallout, MessageVariety } from '@sonarsource/echoes-react';
import React, { Component, ComponentType, ErrorInfo, lazy, Suspense, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getWrappedDisplayName } from '../components/hoc/utils';
import { requestTryAndRepeatUntil } from './request';
import useEffectOnce from './useEffectOnce';

export function lazyLoadComponent<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  displayName?: string,
) {
  const LazyComponent = lazy(() =>
    requestTryAndRepeatUntil(factory, { max: 2, slowThreshold: 2 }, () => true),
  );

  function LazyComponentWrapper(props: React.ComponentProps<T>) {
    return (
      <LazyErrorBoundary>
        <Suspense fallback={null}>
          <LazyComponent {...props} />
        </Suspense>
      </LazyErrorBoundary>
    );
  }

  LazyComponentWrapper.displayName = displayName;
  return LazyComponentWrapper;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class LazyErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      // eslint-disable-next-line no-console
      console.error(
        `
##################################
Error caught by LazyErrorBoundary:
----------------------------------
Thrown error:`,
        error,
        `
----------------------------------
Component stack: ${info.componentStack}
##################################
`,
      );
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <MessageCallout variety={MessageVariety.Danger}>
          <FormattedMessage id="default_component_error_message" />
        </MessageCallout>
      );
    }
    return this.props.children;
  }
}

/**
 * This is a MacGyver fix to solve [SC-39549](https://sonarsource.atlassian.net/browse/SC-39549).
 * Basically using two paperclips and duct tape to prevent an infinite rendering loop.
 */
export function withLazyLoadingDelay<P extends {}>(WrappedComponent: ComponentType<P>) {
  function Wrapper(props: P) {
    const [ready, setReady] = useState(false);

    useEffectOnce(() => {
      setReady(true);
    });

    return ready && <WrappedComponent {...props} />;
  }

  Wrapper.displayName = getWrappedDisplayName(WrappedComponent, 'lazyLoaded');

  return Wrapper as ComponentType<P>;
}
